import * as React from "react"
import { AnchorHTMLAttributes } from "react"

import { Icon } from "../icon"
import * as dropStyles from "./dropdown.css"
import * as typoStyles from "./typography.css"

type ColorType = "indigo" | "teal" | "white" | undefined

export const Panel = (props: any) => {
  const themeToUse = Boolean(props.lightTheme) ? "light" : "dark"
  return (
    <div
      {...props}
      className={`
        ${dropStyles.Panel[themeToUse as ("dark" | "light")]} 
        ${props.col ? dropStyles.Panel.col : ``} 
        ${props.isDeveloperSection ? dropStyles.Panel.isDeveloper : ``} 
        ${props.isDeveloperSection && props.col ? dropStyles.Panel.colDev : ``} 
      `}
      style={{
        width: props.width ? `${props.width}px` : `initial`,
      }}
      suppressHydrationWarning
    >
        {props.children}
    </div>
  )
}

export const SectionHeader = (props: any) => {
  const themeToUse: ("light" | "dark") = props.lightTheme ? "light" : "dark"
  return (
    <h4 className={`${dropStyles.SectionHeader[themeToUse]} ${typoStyles.HeadingSpan}`} {...props}>{props.children}</h4>
  )
}

export const ArticlesHeader = ({ ...rest }: any) => {
  return (
    <h4 className={`${dropStyles.ArticlesHeader} ${typoStyles.HeadingSpan}`} {...rest}>{rest.children}</h4>
  )
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  lightTheme: boolean
  active?: boolean
  external?: boolean
  color?: ColorType
}

export const Link = ({
  active,
  external,
  children,
  color,
  ...rest
}: LinkProps) => {
  return (
    <a
      {...rest}
      className={`
        ${dropStyles.StyledLink[color as ("indigo" | "teal")]} 
        ${active ? dropStyles.StyledLink[`active${color as ("indigo" | "teal")}`] : ``} 
      `}
      target={external ? "_blank" : "_self"}
    >
      {children}
      {external && (
        <Icon
          size="1rem"
          color="currentColor"
          icon={"fa-regular fa-arrow-up-right"}
        />
      )}
    </a>
  )
}

const LinkTitle = (props: any) => <div {...props}>{props.children}</div>

type IconLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
  icon: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  titleOnlyOnMobile?: boolean
  active?: boolean
  external?: boolean
  col?: boolean
  lightTheme: boolean
  hideInLargeDevice?: boolean
  label?: React.ReactNode
  color?: ColorType
}
export const IconLink = ({
  href,
  icon,
  title,
  label,
  subtitle,
  titleOnlyOnMobile = false,
  active,
  lightTheme,
  col,
  external,
  color = "indigo",
  hideInLargeDevice,
  ...rest
}: IconLinkProps) => {
  return (
    <a
      {...rest}
      color={color}
      href={href}
      {...(external ? {target: "_blank", rel: "noopener"} : {})}
      className={`
        ${dropStyles.ArticleLinkStyles[color as "teal" | "indigo"]} 
        ${dropStyles.IconLink[color as "teal" | "indigo"]} 
        ${col ? dropStyles.IconLinkCol : ``} 
        ${hideInLargeDevice ? dropStyles.IconLinkHideInLargeDevice : ``} 
      `}
    >
      <div className={`
        ${dropStyles.LinkIconBase} 
        ${active ? dropStyles.ActiveLinkIcon[color as ("indigo" | "teal")] : ``} 
      `}>{icon}</div>
      <div>
        <LinkTitle className={`
          ${typoStyles.LinkSpan}
          ${dropStyles.LinkTitleOp.base} 
          ${titleOnlyOnMobile ? dropStyles.LinkTitleOp.titleOnlyOnMobile : ``} 
          ${active ? dropStyles.ActiveLinkTitle[color as ("indigo" | "teal")] : ``} 
        `}>
          {label ? (
            <div className={dropStyles.LinkTitleLabel}>
              {title}
              <span className={dropStyles.Label[color as "teal" | "indigo"]}>{label}</span>
            </div>
          ) : (
            title
          )}

          {external && (
            <Icon
              color="currentColor"
              icon={"fa-regular fa-arrow-up-right"}
              size="1rem"
            />
          )}
        </LinkTitle>
        {subtitle && <div className={`
          ${dropStyles.LinkSubtitle} 
          ${active ? dropStyles.ActiveLinkSubtitle[color ? color as ("indigo" | "teal"): "indigo"] : ``}
        `}>{subtitle}</div>}
      </div>
    </a>
  )
}

type ArticleLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  image: React.ReactNode
  title: React.ReactNode
  color?: ColorType
}

export const ArticleLink = ({
  image,
  title,
  color,
  ...rest
}: ArticleLinkProps) => {
  return (
    <a {...rest} color={color} className={dropStyles.ArticleLinkStyles[color ? (color as ("indigo" | "teal")) : "indigo"]}>
      <div className={dropStyles.ArticleImage}>{image}</div>
      <p className={`${dropStyles.ArticleLinkText[color as ("indigo" | "teal")]} ${typoStyles.TextSpan}`}>{title}</p>
    </a>
  )
}


export const HorizontalDivider = (props: any) =>
  <div {...props} className={dropStyles.HorizontalDividerStyle}>{props.children}</div>

export const VerticalDivider = (props: any) => <div {...props} className={dropStyles.VerticalDividerStyle}>{props.children}</div>


export const Spacer = (props: any) =>
  <div
    {...props}
    className={dropStyles.SpacerStyle}
    style={{
      width: props.width ? `${props.width}px` : `initial`,
      height: props.height ? `${props.height}px` : `initial`,
    }}>
      {props.children}
  </div>
